import React, { useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import AuthBackToHome from '@features/auth/components/AuthBackToHome';
import { ErrorResponse } from '@core/types/errorResponse';
import { checkUserEligibility } from '@features/auth/api/authApi';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import '../login/login.css';
import loginImage from '../login/ntcog-logo.png';

interface EmailVerificationProps {
  onVerificationSuccess: (email: string, verificationCode: string, invitationCode?: string) => void;
}

interface EmailForm {
  email: string;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ onVerificationSuccess }) => {
  const [form, setForm] = useState<EmailForm>({ email: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Partial<Record<string, string[]>>>({});
  const showError = useApiError();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      setForm(prev => ({ ...prev, [name]: value }));
    } else if (name === 'verification_code') {
      setVerificationCode(value);
    }

    // Clear error when field changes
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validateEmail = (): boolean => {
    const errors: Partial<Record<string, string[]>> = {};
    
    if (!form.email.trim()) {
      errors.email = ['Email is required'];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = ['Please enter a valid email address'];
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCode = (): boolean => {
    const errors: Partial<Record<string, string[]>> = {};
    
    if (!verificationCode.trim()) {
      errors.verification_code = ['Verification code is required'];
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    
    if (!validateEmail()) {
      return;
    }
    
    setLoading(true);

    try {
      const response = await checkUserEligibility(form.email);
      
      if (response.isSuccess()) {
        const data = response.getData();
        
        // Handle different next_steps responses
        if (data?.next_steps === 'SIGNUPREQUIRED') {
          // User needs to sign up, verification code sent
          setShowCodeInput(true);
          setFormErrors({});
          showSuccess('Verification code sent to your email');
        } else if (data?.next_steps === 'LOGINREQUIRED') {
          // User already has an account, redirect to login
          showSuccess('You already have an account. Redirecting to login...');
          setTimeout(() => {
            navigate('/login');
          }, 1500);
        } else {
          // Unknown response
          setFormErrors({
            email: ['Unexpected response. Please try again.']
          });
        }
      } else {
        const error = response.getError();
        if (response.status === 401) {
          setFormErrors({
            email: ['This email does not have a valid invitation. Please contact your administrator to request an invitation.']
          });
        } else {
          setFormErrors(error?.errors || { email: ['Unable to verify email'] });
        }
      }
    } catch (error) {
      setFormErrors({
        email: ['Unable to process request. Please try again.']
      });
      showError(error as ErrorResponse);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    
    if (!validateCode()) {
      return;
    }
    
    // Proceed to next step with email and verification code
    onVerificationSuccess(form.email, verificationCode);
  };

  return (
    <div className="login-wrapper min-vh-100 d-flex flex-column">
      <AuthBackToHome />
      <main
        id="main"
        className="flex-grow-1 d-flex align-items-center justify-content-center py-4"
        tabIndex={-1}
        data-testid="register-email-step-page"
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="login-card shadow-lg border-0" data-testid="register-email-step-card">
                <Row className="g-0">
                  <Col
                    md={6}
                    className="d-none d-md-flex align-items-center justify-content-center login-image bg-light"
                    data-testid="register-email-step-illustration-panel"
                  >
                    <img src={loginImage} alt="NTCOG Logo" className="img-fluid" />
                  </Col>

                  <Col md={6} className="d-flex align-items-center">
                    <Card.Body className="p-4 p-md-5 w-100 auth-card__body">
                      <h1 className="mb-4 text-center fw-bold text-dark auth-page-heading">Create Account</h1>
                      <p className="text-center text-muted mb-4">
                        Step 1 of 2: Verify your email
                      </p>

                      {!showCodeInput ? (
                        <Form
                          noValidate
                          onSubmit={handleSubmitEmail}
                          className="login-form"
                          aria-label="Register email verification form"
                          data-testid="register-email-form"
                        >
                          <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email address <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              placeholder="Enter your email address"
                              value={form.email}
                              onChange={handleChange}
                              autoComplete="email"
                              isInvalid={!!formErrors.email}
                              data-testid="register-email-input"
                            />
                            <Form.Control.Feedback type="invalid" role="alert">
                              {formErrors.email?.[0]}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              Enter the email address that received the invitation
                            </Form.Text>
                          </Form.Group>

                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-100 mb-3"
                            disabled={loading}
                            aria-busy={loading}
                            data-testid="register-email-submit-button"
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Checking eligibility…
                              </>
                            ) : (
                              'Continue'
                            )}
                          </Button>
                        </Form>
                      ) : (
                        <Form
                          noValidate
                          onSubmit={handleSubmitCode}
                          className="login-form"
                          aria-label="Register verification code form"
                          data-testid="register-code-form"
                        >
                          <div className="alert alert-success mb-4" data-testid="register-code-success-alert">
                            Verification code sent to {form.email}
                          </div>

                          <Form.Group className="mb-4" controlId="verification_code">
                            <Form.Label>Verification Code <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              name="verification_code"
                              placeholder="Enter your verification code"
                              value={verificationCode}
                              onChange={handleChange}
                              isInvalid={!!formErrors.verification_code}
                              autoFocus
                              data-testid="register-code-input"
                            />
                            <Form.Control.Feedback type="invalid" role="alert">
                              {formErrors.verification_code?.[0]}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              Check your email for the verification code
                            </Form.Text>
                          </Form.Group>

                          <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
                            <Button
                              variant="outline-secondary"
                              size="lg"
                              className="w-100 flex-sm-fill"
                              onClick={() => {
                                setShowCodeInput(false);
                                setVerificationCode('');
                                setFormErrors({});
                              }}
                              disabled={loading}
                              data-testid="register-code-back-button"
                            >
                              Back
                            </Button>
                            <Button
                              type="submit"
                              variant="primary"
                              size="lg"
                              className="w-100 flex-sm-fill"
                              disabled={loading}
                              aria-busy={loading}
                              data-testid="register-code-submit-button"
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Verifying…
                                </>
                              ) : (
                                'Verify & Continue'
                              )}
                            </Button>
                          </div>
                        </Form>
                      )}

                      <div className="auth-secondary-link-wrap">
                        <Button
                          variant="link"
                          className="text-muted p-0 forgot-password auth-secondary-link"
                          onClick={() => navigate('/login')}
                          data-testid="register-login-link"
                        >
                          Already have an account? Login
                        </Button>
                      </div>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export default EmailVerification;